import axiosInstance from "../utils/axios";
import { getCompanyIdFromToken } from "../utils/tokenUtils";

/**
 * Get all leads sources
 * @param {number} pageNumber - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @param {string} search - Search term
 * @returns {Promise} Response with array of sources
 */
export const getLeadsSources = async (pageNumber = 1, pageSize = 10000, search = "") => {
  try {
    const response = await axiosInstance.get(`/api/FormDesk/getLeadSource`, {
      params: {
        pageNumber,
        pageSize,
        search,
      },
    });
    const responseData = response.data;
    let raw = responseData?.data ?? responseData ?? [];
    if (typeof raw === "string") {
      try {
        raw = JSON.parse(raw);
      } catch (_) {
        raw = [];
      }
    }
    const sources = Array.isArray(raw) ? raw : [];
    let totalCount = 0;
    if (responseData?.totalRecords !== undefined) {
      totalCount = responseData.totalRecords;
    } else if (sources.length > 0) {
      const first = sources[0];
      totalCount = first.totalRecords;
    }
    return {
      success: true,
      data: sources,
      totalCount,
      totalRecords: totalCount,
      message: "Leads sources fetched successfully",
    };
  } catch (error) {
    console.error("Error fetching leads sources:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch leads sources",
      data: [],
      totalCount: 0,
    };
  }
};

/**
 * Add or Edit a Lead Source
 * @param {Object} sourceData { id, sourceName }
 * @returns {Promise} Response with source data
 */
export const addEditLeadSource = async (sourceData) => {
  try {
    const payload = {
      sourceName: sourceData.sourceName,
    };

    if (sourceData.id) {
      payload.id = sourceData.id;
    }

    // Note: The screenshot showed GET but typically with a body it's POST. 
    // If it is truly a GET request with a body, we use axios config, 
    // otherwise we use axiosInstance.post
    const response = await axiosInstance({
      method: 'POST', // Try POST first as it's standard for add/edit with body
      url: `/api/FormDesk/addEditLeadSource`,
      data: payload
    });

    const responseData = response.data;
    if (responseData.statusCode === 200 || responseData.success) {
      let rawData = responseData.data;
      if (typeof rawData === "string") {
        try {
          rawData = JSON.parse(rawData);
        } catch (_) {}
      }
      return {
        success: true,
        data: rawData,
        message: responseData.message || "Source saved successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to save source");
    }
  } catch (error) {
    console.error("Error in addEditLeadSource:", error);
    return {
      success: false,
      error: error?.response?.data?.message || error.message || "Failed to save source",
      data: null,
    };
  }
};

/**
 * Delete a Lead Source
 * @param {string} sourceId ID of the source to delete
 * @returns {Promise} Response with result
 */
export const deleteLeadSource = async (sourceId) => {
  try {
    const response = await axiosInstance.post(
      `/api/FormDesk/deleteLeadSource`,
      `"${sourceId}"`, // Send as a JSON string per Swagger schema
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    const responseData = response.data;
    if (responseData.statusCode === 200 || responseData.success) {
      return {
        success: true,
        data: sourceId,
        message: responseData.message || "Source deleted successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to delete source");
    }
  } catch (error) {
    console.error("Error in deleteLeadSource:", error);
    return {
      success: false,
      error: error?.response?.data?.message || error.message || "Failed to delete source",
      data: null,
    };
  }
};
